import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import Button from '@atlaskit/button';
import { FlagGroup } from '@atlaskit/flag';
import { reportFeedback } from '@atlassian/temp-proxies';
import { useApplicationContext } from '../../../common-context';
import { NotificationFlag, NotificationFlagType } from '../../../notification-flag';
import { PanelTypeEnum } from '../constants';
import { ChatRoomLink } from '../help-links';
import { getEnvironment, DEVELOPMENT, STAGING, PRODUCTION } from '@atlassian/temp-shared';
import { FeedbackForm } from '../../../feedback-form';
import { AddScreenshot } from '../../../add-screenshot-util/src/add-screenshot-util';
import { FlagTypeEnum } from '../../../constants';
import './error-reporting.scss';

const ErrorTitle = 'Report issue on Slack';

interface Payload {
  userName: string;
  userEmail: string;
  AAID: string;
  userInput: string;
  appName: string;
  errorPageURL: string;
  errorMessage?: string;
  type?: string;
  splunkQueryUrl?: string;
  shouldCreateSupportTicket?: boolean;
  summary?: string;
}

function getEnvironmentName() {
  switch (getEnvironment()) {
    case DEVELOPMENT:
      return 'ddev';
    case STAGING:
      return 'stg-east';
    case PRODUCTION:
      return 'prod-east';
    default:
      return 'local';
  }
}

function getServiceName(serviceName: string) {
  switch (serviceName) {
    case 'css-xp-ans':
      return 'css-xp-ans-svc';
    case 'css-xp-auth':
      return 'css-xp-auth-service';
    case 'css-xp-apr-desk-svc':
      return 'css-xp-apr-desk-svc';
    case 'css-xp-sell-svc':
      return 'css-xp-sell-svc';
    default:
      return 'css-xp-acserver';
  }
}
function getSplunkUrl(microServiceName: string, envName: string, requestTraceId: string) {
  const splunkUrl = `https://splunk.paas-inf.net/en-US/app/search/search?q=search \`micros_${microServiceName}\` (application m.t=application NOT micros_container=platform-* NOT micros_container=*-sidecar) (env=${envName}) ${requestTraceId}&display.page.search.mode=smart&dispatch.sample_ratio=1&earliest=-30d@d&latest=now`;
  return splunkUrl;
}

const ErrorPreamble = (props: { product: string }) => {
  return (
    <p>
      Please describe the problem you are experiencing. This message will be sent to{' '}
      {props.product === 'ac' ? <ChatRoomLink /> : 'help channel'} with additional details.
    </p>
  );
};

export const ReportError = (props: {
  errorMessage?: string;
  type?: PanelTypeEnum;
  // requestTraceId and serviceName are used to make splunk query url
  // usual values for serviceName are css-xp-ans,css-xp-auth, css-xp-apr-desk-svc ,etc.
  // requestTraceId is a UUID to track requests
  requestTraceId?: string;
  serviceName?: string;
  enableTicketCreationOption?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayFlag, setDisplayFlag] = useState(false);
  const [flagType, setFlagType] = useState<NotificationFlagType>(FlagTypeEnum.SUCCESS);
  const [screenshot, setScreenshot] = useState<File | undefined>(undefined);
  const [files, setFiles] = useState<File[]>([]);

  const open = async () => {
    if (props.type === PanelTypeEnum.ERROR_PANEL || props.type === PanelTypeEnum.INFO_PANEL) {
      try {
        const canvas = await html2canvas(document.body, {
          x: window.scrollX,
          y: window.scrollY,
          width: window.innerWidth,
          height: window.innerHeight,
        });
        const imgData = canvas.toDataURL('image/png');
        await fetch(imgData)
          .then((response) => response.blob())
          .then((blob) => {
            const imgFile = new File([blob], 'screenshot.png', { type: blob.type });
            setScreenshot(imgFile);
          });
      } catch (error) {
        // console.log(error);
      }
    }
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
    setScreenshot(undefined);
    setFiles([]);
  };
  const showFlag = () => setDisplayFlag(true);
  const hideFlag = () => setDisplayFlag(false);
  const context = useApplicationContext();

  const handleFileChange = (files: File[]) => {
    setFiles(files);
  };

  const submitForm = async (data: {
    summary: string;
    description: string;
    additionalOption1?: boolean;
    additionalOption2?: boolean;
  }) => {
    const { summary, description, additionalOption1: consent, additionalOption2: shouldCreateSupportTicket } = data;
    if (context) {
      const payload: Payload = {
        userName: context.displayName,
        userEmail: context.email,
        AAID: context.atlassianAccountId,
        userInput: description,
        appName: context.product,
        errorPageURL: window.location.href,
        errorMessage: props.errorMessage,
        type: props.type,
        shouldCreateSupportTicket: shouldCreateSupportTicket,
        summary: summary,
      };

      if (props.requestTraceId && props.serviceName) {
        const microserviceName = getServiceName(props.serviceName);
        const envName = getEnvironmentName();
        payload.splunkQueryUrl = getSplunkUrl(microserviceName, envName, props.requestTraceId);
      }

      let response;
      if (consent && screenshot) {
        response = await reportFeedback(payload, [screenshot, ...files]);
      } else {
        response = await reportFeedback(payload, files);
      }
      response === 200 ? setFlagType(FlagTypeEnum.SUCCESS) : setFlagType(FlagTypeEnum.ERROR);
      close();
      showFlag();
    }
  };

  const handlePropagation = (e: React.FormEvent<HTMLFormElement>) => {
    // To prevent submitting the parent form added stop propagation here
    // for example in change subscription pane it is getting submitted.
    e.stopPropagation();
  };

  // to improve : Get this success message from parent component
  const getSuccessMessage = () => {
    return <>We have submitted your request on the {context.product === 'ac' ? <ChatRoomLink /> : 'help channel'}</>;
  };

  const getErrorMessage = () => {
    return <>There was an error while reporting your issue. Please try again later.</>;
  };

  return (
    <div
      onClick={(e) => {
        // added stopPropagation because clicking anywhere on the modal was closing it due to the
        // onClose getting triggered for the open popup dropdown in PPC navigation bar profile button
        e.stopPropagation();
      }}
      className="reporting-container"
    >
      {props.type && props.type === PanelTypeEnum.INFO_PANEL && (
        <div className="info-panel-report-error">
          <Button appearance="link" onClick={open}>
            Report issue on Slack
          </Button>
        </div>
      )}
      {props.type && props.type === PanelTypeEnum.ERROR_PANEL && (
        <div className="error-panel-report-error">
          <Button appearance="primary" onClick={open}>
            Report issue on Slack
          </Button>
        </div>
      )}

      {isOpen && (
        <form onSubmit={handlePropagation}>
          <FeedbackForm
            onClose={close}
            onSubmit={submitForm}
            feedbackTitle={ErrorTitle}
            feedbackTitleDetails={<ErrorPreamble product={context.product} />}
            customContent={<AddScreenshot onFileSelected={handleFileChange} />}
            showCheckbox1={true}
            showCheckbox2={props.enableTicketCreationOption}
            defaultChecked1={true}
            showSummary={true}
            summaryPlaceholder="A summary of your feedback in few words."
            additionalOptionLabel1="Include automatic snapshot of the error to help with reporting"
            additionalOptionLabel2="Automatically create a support ticket for this issue"
            descriptionPlaceholder={'Brief description of the issue you are experiencing'}
            submitButtonLabel={'Send'}
            cancelButtonLabel={'Cancel'}
          />
        </form>
      )}
      <FlagGroup onDismissed={hideFlag}>
        {displayFlag && (
          <NotificationFlag
            id="error-report"
            header={flagType === FlagTypeEnum.SUCCESS ? 'Issue reported successfully' : 'Something went wrong'}
            type={flagType}
            message={flagType === FlagTypeEnum.SUCCESS ? getSuccessMessage() : getErrorMessage()}
          />
        )}
      </FlagGroup>
    </div>
  );
};
